import { useState } from "react";
import {
  Card, Table, Button, Tag, Select, Input, Drawer,
} from "antd";
import { RefreshCcw } from "lucide-react";
import { useSyncQueueList } from "../../../api-hooks/syncQueue";
import { TSyncQueueItem, TSyncStatus } from "../../../types";
import type { ColumnsType } from "antd/es/table";

// Offline-first sync queue — field worker app queues operations when offline.
// Status 'conflict' means two devices edited the same record simultaneously.

const { Option } = Select;

const STATUS_COLOR: Record<TSyncStatus, string> = {
  pending: "default",
  processing: "blue",
  applied: "green",
  conflict: "orange",
  failed: "red",
};

const OPERATION_COLOR: Record<TSyncQueueItem["operation"], string> = {
  create: "green",
  update: "blue",
  delete: "red",
};

const STATUS_OPTIONS: Array<"all" | TSyncStatus> = [
  "all", "pending", "processing", "applied", "conflict", "failed",
];

export default function SyncQueue() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<TSyncQueueItem | null>(null);

  const queryParams = {
    limit: 50,
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(entityTypeFilter.trim() ? { entity_type: entityTypeFilter.trim() } : {}),
  };

  const { data, isLoading, isFetching, refetch } = useSyncQueueList(queryParams);
  const items: TSyncQueueItem[] = data?.data ?? [];

  const columns: ColumnsType<TSyncQueueItem> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      render: (val: number) => <span className="text-xs text-slate-400">#{val}</span>,
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (val: string | null) => (
        <span className="text-xs text-slate-700">{val || <span className="italic text-slate-300">—</span>}</span>
      ),
    },
    {
      title: "User",
      dataIndex: "user_name",
      key: "user_name",
      render: (val: string | null) => (
        <span className="text-xs text-slate-500">{val || <span className="italic text-slate-300">—</span>}</span>
      ),
    },
    {
      title: "Device ID",
      dataIndex: "device_id",
      key: "device_id",
      render: (val: string) => (
        // Truncated device_id — full value shown in row drawer
        <span className="font-mono text-xs text-slate-500 max-w-[100px] block truncate" title={val}>{val}</span>
      ),
    },
    {
      title: "Operation",
      dataIndex: "operation",
      key: "operation",
      render: (val: TSyncQueueItem["operation"]) => (
        <Tag color={OPERATION_COLOR[val]}>{val}</Tag>
      ),
    },
    {
      title: "Entity Type",
      dataIndex: "entity_type",
      key: "entity_type",
      render: (val: string) => (
        <span className="font-mono text-xs text-slate-700">{val}</span>
      ),
    },
    {
      title: "Entity UUID",
      dataIndex: "entity_uuid",
      key: "entity_uuid",
      render: (val: string) => (
        <span className="font-mono text-xs text-slate-400 max-w-[120px] block truncate" title={val}>{val}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val: TSyncStatus) => <Tag color={STATUS_COLOR[val]}>{val}</Tag>,
    },
    {
      title: "Retries",
      dataIndex: "retry_count",
      key: "retry_count",
      render: (val: number) => (
        <span className="text-xs text-slate-500">{val}</span>
      ),
    },
    {
      title: "Queued",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleString("en-GB")}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Sync Queue Monitor</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Offline-first sync queue — field worker operations. Click a row to inspect payload. Showing last 50 records.
          </p>
        </div>
        <Button
          icon={<RefreshCcw size={14} />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Status:</span>
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
            {STATUS_OPTIONS.map((s) => (
              <Option key={s} value={s}>{s === "all" ? "All Statuses" : s}</Option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Entity Type:</span>
          <Input
            placeholder="e.g. job, timesheet..."
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            style={{ width: 180 }}
            allowClear
          />
        </div>
        {data?.extra && (
          <span className="text-xs text-slate-400 ml-2">{data.extra.total} total</span>
        )}
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        {/* No client-side pagination — server returns max 50 records, use filters to narrow down */}
        <Table
          dataSource={items}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading || isFetching}
          size="middle"
          scroll={{ x: 1100 }}
          locale={{ emptyText: "No sync queue items found for the selected filters." }}
          rowClassName={(record) =>
            record.status === "conflict"
              ? "bg-orange-50/40 cursor-pointer"
              : record.status === "failed"
              ? "bg-red-50/40 cursor-pointer"
              : "cursor-pointer"
          }
          onRow={(record) => ({
            onClick: () => setSelectedItem(record),
          })}
        />
      </Card>

      {/* Payload Drawer */}
      <Drawer
        title={
          <div className="flex items-center gap-2">
            <span>Sync Item #{selectedItem?.id}</span>
            {selectedItem && (
              <Tag color={STATUS_COLOR[selectedItem.status]}>{selectedItem.status}</Tag>
            )}
          </div>
        }
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        width={520}
      >
        {selectedItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-slate-400 text-xs mb-0.5">Operation</div>
                <Tag color={OPERATION_COLOR[selectedItem.operation]}>{selectedItem.operation}</Tag>
              </div>
              <div>
                <div className="text-slate-400 text-xs mb-0.5">Entity Type</div>
                <span className="font-mono text-xs">{selectedItem.entity_type}</span>
              </div>
              <div className="col-span-2">
                <div className="text-slate-400 text-xs mb-0.5">Entity UUID</div>
                <span className="font-mono text-xs break-all">{selectedItem.entity_uuid}</span>
              </div>
              <div className="col-span-2">
                <div className="text-slate-400 text-xs mb-0.5">Device ID</div>
                <span className="font-mono text-xs break-all">{selectedItem.device_id}</span>
              </div>
              {selectedItem.checksum && (
                <div className="col-span-2">
                  <div className="text-slate-400 text-xs mb-0.5">Checksum</div>
                  <span className="font-mono text-xs break-all">{selectedItem.checksum}</span>
                </div>
              )}
            </div>

            <div>
              <div className="text-slate-500 text-xs font-medium mb-1">Payload</div>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono overflow-auto max-h-60 text-slate-700">
                {JSON.stringify(selectedItem.payload, null, 2)}
              </pre>
            </div>

            {selectedItem.conflict_data && (
              <div>
                <div className="text-orange-500 text-xs font-medium mb-1">Conflict Data</div>
                <pre className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs font-mono overflow-auto max-h-48 text-slate-700">
                  {JSON.stringify(selectedItem.conflict_data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
